import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, tap, filter } from 'rxjs';
import { isHttpErrorResponse } from '@ipg/ngx-frontend-toolset';
import { MediaPickerDialogService } from '../../components/media-picker-dialog/services/media-picker-dialog.service';
import { MediaItem } from '../../models/media.model';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperIcon,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Router } from '@angular/router';
import { catchError, finalize, throwError } from 'rxjs';

import { UnitResponseModel } from '../units/units.model';
import { UnitService } from '../units/services/unit.service';
import { UnitMoveService } from '../settings/services/unit-move.service';
import {
  ActiveMoveStatus,
  BrokenMediaItem,
  MediaResolution,
  UnitMovePreviewResponse,
} from '../settings/models/move-unit.models';

interface MediaRow extends BrokenMediaItem {
  action: 'replace' | 'copy';
  targetMediaId?: string;
  targetMediaLabel?: string;
}

@Component({
  selector: 'app-settings-actions-move-unit',
  standalone: true,
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperIcon,
    MatStepperNext,
    MatStepperPrevious,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
  ],
  templateUrl: './settings-actions-move-unit.component.html',
  styleUrl: './settings-actions-move-unit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsActionsMoveUnitComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _units = inject(UnitService);
  private readonly _moveService = inject(UnitMoveService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _mediaPicker = inject(MediaPickerDialogService);

  protected readonly currentUnit = this._units.currentUnit;
  protected readonly allUnits = signal<UnitResponseModel[]>([]);
  protected readonly selectedTargetPrevId = signal<string | null>(null);
  protected readonly preview = signal<UnitMovePreviewResponse | null>(null);
  protected readonly mediaRows = signal<MediaRow[]>([]);
  protected readonly isLoadingPreview = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly maxStepReached = signal(0);
  protected readonly activeMove = signal<ActiveMoveStatus | null>(null);

  @ViewChild(MatStepper) stepper!: MatStepper;

  protected readonly movingSubtreeIds = computed(() => {
    const me = this.currentUnit();
    if (!me) return new Set<string>();
    const ids = new Set<string>([me._id]);
    const byId = new Map(this.allUnits().map((u) => [u._id, u]));
    const visit = (id: string) => {
      const u = byId.get(id);
      if (!u) return;
      for (const child of u.nextUnitIds) {
        if (!ids.has(child)) {
          ids.add(child);
          visit(child);
        }
      }
    };
    visit(me._id);
    return ids;
  });

  protected readonly availableParents = computed(() => {
    const me = this.currentUnit();
    if (!me) return [];
    const subtree = this.movingSubtreeIds();
    return this.allUnits().filter(
      (u) =>
        !subtree.has(u._id) &&
        u._id !== me.prevUnitId &&
        !u.deleteAt &&
        !u.moveId,
    );
  });

  protected readonly sourceParent = computed(() => {
    const me = this.currentUnit();
    if (!me) return null;
    return this.allUnits().find((u) => u._id === me.prevUnitId) ?? null;
  });

  protected readonly selectedParent = computed(() => {
    const id = this.selectedTargetPrevId();
    return this.allUnits().find((u) => u._id === id) ?? null;
  });

  protected readonly hasBrokenMedia = computed(() => {
    const p = this.preview();
    return p != null && p.brokenMedia.length > 0;
  });

  protected readonly isMediaStepValid = computed(() => {
    if (!this.preview()) return false;
    return this.mediaRows().every(
      (r) =>
        r.action === 'copy' || (r.action === 'replace' && !!r.targetMediaId),
    );
  });

  protected readonly mediaCopyCount = computed(
    () => this.mediaRows().filter((r) => r.action === 'copy').length,
  );

  protected readonly mediaReplaceCount = computed(
    () => this.mediaRows().filter((r) => r.action === 'replace').length,
  );

  ngOnInit(): void {
    this._units.getByUser().subscribe({
      next: (units) => this.allUnits.set(units),
    });

    this._moveService.getActiveMoveStatus().subscribe({
      next: (active) => this.activeMove.set(active),
    });
  }

  reCheckActiveMove(): void {
    this._moveService.getActiveMoveStatus().subscribe({
      next: (active) => this.activeMove.set(active),
    });
  }

  onTargetSelected(): void {
    const me = this.currentUnit();
    const targetId = this.selectedTargetPrevId();
    if (!me || !targetId) return;

    this.isLoadingPreview.set(true);
    this.preview.set(null);
    this.mediaRows.set([]);

    this._moveService
      .getMovePreview(me._id, targetId)
      .pipe(finalize(() => this.isLoadingPreview.set(false)))
      .subscribe({
        next: (preview) => {
          this.preview.set(preview);
          this.mediaRows.set(
            preview.brokenMedia.map((m) => ({
              ...m,
              action: 'copy' as const,
            })),
          );
        },
        error: () => {
          this._snackbar.open(
            'Er is iets misgegaan bij het laden van het voorbeeld',
            'OK',
          );
        },
      });
  }

  onMediaActionChange(index: number, action: 'replace' | 'copy'): void {
    this.mediaRows.update((rows) => {
      const updated = [...rows];
      updated[index] = {
        ...updated[index],
        action,
        targetMediaId: undefined,
        targetMediaLabel: undefined,
      };
      return updated;
    });
  }

  openMediaPicker(index: number): void {
    const row = this.mediaRows()[index];
    if (!row) return;

    this._mediaPicker.open(
      (result) =>
        of(result).pipe(
          filter((r): r is MediaItem => r != null),
          tap((media) => this._setMediaPick(index, media)),
        ),
      {
        type: row.type,
        context: { scope: 'unit' },
        unitOnly: true,
      },
    );
  }

  private _setMediaPick(index: number, media: MediaItem): void {
    this.mediaRows.update((rows) => {
      const updated = [...rows];
      updated[index] = {
        ...updated[index],
        targetMediaId: media.id,
        targetMediaLabel: media.label,
      };
      return updated;
    });
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.maxStepReached.update((current) =>
      Math.max(current, event.selectedIndex),
    );
  }

  submit(): void {
    const me = this.currentUnit();
    const targetPrevUnitId = this.selectedTargetPrevId();
    if (!me || !targetPrevUnitId) return;

    // Re-check active move before submitting.
    this._moveService.getActiveMoveStatus().subscribe({
      next: (active) => {
        if (active) {
          this.activeMove.set(active);
          this._snackbar.open(
            'Er is al een verplaatsing bezig. Wacht tot deze is afgerond.',
            'OK',
          );
          return;
        }
        this._performMove(me._id, targetPrevUnitId);
      },
    });
  }

  private _performMove(unitId: string, targetPrevUnitId: string): void {
    const mediaResolutions: MediaResolution[] = this.mediaRows().map((r) => ({
      sourceMediaId: r._id,
      action: r.action,
      ...(r.action === 'replace' && r.targetMediaId
        ? { targetMediaId: r.targetMediaId }
        : {}),
    }));

    this.isSubmitting.set(true);

    this._moveService
      .moveUnit(unitId, { targetPrevUnitId, mediaResolutions })
      .pipe(
        tap(() => {
          this._snackbar.open(
            'De unit wordt verplaatst. Dit kan enkele minuten duren.',
            'OK',
          );
          this._router.navigate(['/settings/general']);
        }),

        catchError((error) => {
          // 409 UNIT_MOVE_ALREADY_ACTIVE — another move was started
          // between when we re-checked and when we submitted. Surface
          // the same banner copy the user already saw.
          if (
            isHttpErrorResponse(error) &&
            error.error?.detail?.errorCode === 'UNIT_MOVE_ALREADY_ACTIVE'
          ) {
            this._snackbar.open(
              'Er is al een verplaatsing bezig. Wacht tot deze is afgerond.',
              'OK',
            );
            this.reCheckActiveMove();
          } else {
            this._snackbar.open(
              'Er is iets misgegaan bij het verplaatsen. Probeer het later nog eens.',
              'OK',
            );
          }
          return throwError(() => error);
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  cancel(): void {
    this._router.navigate(['/settings/general']);
  }
}
